/**
 * AS DICAS SOB DEMANDA — pontos pulsando nas funções e o mini tour de cada uma.
 *
 * Os pontos só aparecem para quem já fechou o tour completo (`registro`), para
 * funções ainda não vistas, e nunca por cima de outro tour, da Ajuda ou de um
 * diálogo. Ficam em `position: fixed` (sem tela nova, sem rota) e seguem o alvo.
 */
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useChatStore } from '@/stores/chatStore'
import { ITENS_DO_TOUR, useGuidedTourStore, type ItemDoTour } from '@/stores/guidedTourStore'
import { usePermissionsStore } from '@/stores/permissionsStore'
import { MAXIMO_DE_PONTOS, posicaoDoPonto, type PontoNaTela } from './alvos-das-dicas'
import { esperarElemento } from './esperar-elemento'
import { limparCampoDaMensagem, podeGerirEquipe } from './GuidedTourHost'
import { abrirTour, type TourAberto } from './motor-do-tour'
import { passosDoItem, rotulosDoMiniTour } from './passos-itens'
import { garantirConversaAberta } from './preparar'
import type { RotaDoPasso } from './types'
import './dicas.css'

const ESPERA_DO_ALVO_MS = 2000

function pontosVisiveis(): PontoNaTela[] {
  const { registro, ativo, ajudaAberta, feitos } = useGuidedTourStore.getState()
  if (registro === null || ativo || ajudaAberta || document.querySelector('[role="dialog"]')) return []
  return ITENS_DO_TOUR.filter((i) => !feitos[i] && (i !== 'equipe' || podeGerirEquipe()))
    .map((i) => posicaoDoPonto(i))
    .filter((p): p is PontoNaTela => p !== null)
    .slice(0, MAXIMO_DE_PONTOS)
}

const igual = (a: PontoNaTela[], b: PontoNaTela[]) =>
  a.length === b.length && a.every((p, i) => p.item === b[i].item && p.top === b[i].top && p.left === b[i].left)

/** Mede os pontos e re-mede quando a tela muda (só enquanto o tour completo já foi fechado). */
function acompanharPontos(setPontos: (f: (antes: PontoNaTela[]) => PontoNaTela[]) => void, ligado: boolean): (() => void) | undefined {
  const medir = () =>
    setPontos((antes) => {
      const novos = pontosVisiveis()
      return igual(antes, novos) ? antes : novos
    })
  let quadro = 0
  const agendar = () => {
    if (!quadro)
      quadro = requestAnimationFrame(() => {
        quadro = 0
        medir()
      })
  }
  medir()
  if (!ligado) return undefined
  const resize = new ResizeObserver(agendar)
  resize.observe(document.body)
  const muda = new MutationObserver(agendar)
  muda.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] })
  window.addEventListener('resize', agendar)
  window.addEventListener('scroll', agendar, true)
  return () => {
    cancelAnimationFrame(quadro)
    resize.disconnect()
    muda.disconnect()
    window.removeEventListener('resize', agendar)
    window.removeEventListener('scroll', agendar, true)
  }
}

export function DicasHost(): ReactElement | null {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const registro = useGuidedTourStore((s) => s.registro)
  const ativo = useGuidedTourStore((s) => s.ativo)
  const ajudaAberta = useGuidedTourStore((s) => s.ajudaAberta)
  const feitos = useGuidedTourStore((s) => s.feitos)
  const pedido = useGuidedTourStore((s) => s.pedidoDeItem)
  const permissoes = usePermissionsStore((s) => s.status)
  const conversaId = useChatStore((s) => s.activeConversationId)
  const [pontos, setPontos] = useState<PontoNaTela[]>([])
  const aberto = useRef<TourAberto | null>(null)
  const pedidoVisto = useRef(pedido?.n ?? 0)

  const irPara = useCallback(
    (rota: RotaDoPasso) => void navigate(rota === 'equipe' ? '/settings/team' : conversaId ? `/chat/${conversaId}` : '/chat'),
    [navigate, conversaId],
  )

  const abrirItem = useCallback(
    async (item: ItemDoTour) => {
      const store = useGuidedTourStore.getState()
      if (store.ativo || aberto.current) return
      store.marcarAtivo(true)
      const passos = passosDoItem(item, t, { limparCampo: limparCampoDaMensagem })
      const alvo = passos[0]?.alvo
      // Sem o alvo na tela (ex.: sem microfone) não há o que mostrar: libera sem fingir tour.
      const pronto = item === 'equipe' || ((await garantirConversaAberta()) && alvo && (await esperarElemento(alvo, ESPERA_DO_ALVO_MS)))
      if (!pronto) return store.marcarAtivo(false)
      aberto.current = abrirTour({
        passos,
        rotulos: rotulosDoMiniTour(t),
        irPara,
        aoFechar: (desfecho) => {
          aberto.current = null
          if (desfecho === 'concluido') useGuidedTourStore.getState().marcarFeito(item)
          useGuidedTourStore.getState().definirFechamento(null)
          useGuidedTourStore.getState().marcarAtivo(false)
        },
      })
      store.definirFechamento(aberto.current.fechar)
    },
    [t, irPara],
  )

  useEffect(() => {
    if (!pedido || pedido.n === pedidoVisto.current) return
    pedidoVisto.current = pedido.n
    void abrirItem(pedido.item)
  }, [pedido, abrirItem])

  useEffect(() => () => aberto.current?.fechar(), [])

  useEffect(() => acompanharPontos(setPontos, registro !== null), [registro, ativo, ajudaAberta, feitos, permissoes, conversaId])

  if (pontos.length === 0) return null
  return (
    <>
      {pontos.map((p) => (
        <button
          key={p.item}
          type="button"
          className="axg-dica"
          data-testid={`dica-${p.item}`}
          aria-label={t(`guidedTour.dicas.${p.item}.aria`)}
          style={{ top: p.top, left: p.left }}
          onClick={() => useGuidedTourStore.getState().pedirItem(p.item)}
        />
      ))}
    </>
  )
}
